import { CrashBreadcrumbs } from '../crash/breadcrumbs.js';
import { analytics, AnalyticsAdNetwork, AnalyticsEventName, AnalyticsPropertyKey } from '../analytics/index.js';
import { AnalyticsAdType } from '../analytics/analytics_types.js';
import { clearAdId, generateAdId, getAdId } from '../analytics/analytics_ad_module.js';
import { logAdRewardedStarted, logInterstitialShown } from '../analytics/appsflyer_events.js';
import { UtilityPlugin } from '../plugins/utility_plugin.js';
import { OfferService } from '../offers/offer_service.js';
import { AdsConfigService } from './ads_config.js';
import { resetInterstitialLevels } from './interstitial_frequency.js';
import { reportAdImpression } from '../api/requests/economy.requests.js';
import { flushEarlyEcpm, recordAdLoaded } from './early_ecpm.js';
import { OfferTriggerController } from '../offers/offer_trigger_controller.js';
import { AppsFlyer } from '../plugins/apps_flyer_plugin.js';
import { audioController } from '../audio/audio_controller.js';
import {
  mapAppLovinConsentDecision,
  type AppLovinInitializationResult,
  type AppsFlyerConsentDecision,
} from '../privacy/applovin_consent_policy.js';
import { S2SEventContextService } from '../s2s/s2s_event_context.js';
import { GoogleAttributionPlugin } from '../plugins/google_attribution_plugin.js';
import { APPLOVIN_CONFIG, resolveAppLovinValue } from './applovin_config.js';
import { NetworkManager } from '../network_manager.js';
import { SessionController } from '../socket_handler/controllers/session_controller.js';

const ADS_LOG_TAG = '[ADS]';
const BANNER_LOG_TAG = '[ADS_BANNER]';
const LIFETIME_REVENUE_STORAGE_KEY = 'applovin_ilr_lifetime_revenue_last_sent';
const LEGACY_LIFETIME_REVENUE_STORAGE_KEY = 'cas_ilr_lifetime_revenue_last_sent';
const AD_NETWORK = AnalyticsAdNetwork.APPLOVIN;
const APPSFLYER_MEDIATION = 'applovin_max';
const INIT_TIMEOUT_MS = 30_000;
const INIT_RETRY_DELAY_MS = 60_000;
const INIT_TIMED_OUT = Symbol( 'init_timed_out' );
const INIT_TIMEOUT_ERROR = 'timeout';

type MaxAdsBridge = {
  initialize( opts: Record<string, unknown> ): Promise<Record<string, unknown>>;
  loadRewardedAd( opts: Record<string, unknown> ): Promise<void>;
  isRewardedAdLoaded(): Promise<{ isLoaded?: boolean }>;
  showRewardedAd( opts?: Record<string, unknown> ): Promise<{ isUserEarnReward?: boolean }>;
  loadInterstitialAd( opts: Record<string, unknown> ): Promise<void>;
  isInterstitialAdLoaded(): Promise<{ isLoaded?: boolean }>;
  showInterstitialAd( opts?: Record<string, unknown> ): Promise<void>;
  loadBannerAd( opts: Record<string, unknown> ): Promise<void>;
  showBannerAd(): Promise<void>;
  hideBannerAd(): Promise<void>;
  showMediationDebugger?(): Promise<void>;
};

function getPlugin(): MaxAdsBridge | null
{
  return ( window as any ).Capacitor?.Plugins?.MaxAds ?? null;
}

async function showFullscreenAdMuted<T>( show: () => Promise<T> ): Promise<T>
{
  audioController.muteForFullscreenAd();
  try
  {
    return await show();
  }
  finally
  {
    audioController.unmuteAfterFullscreenAd();
  }
}

export function isNative(): boolean
{
  const platform = window.Capacitor?.getPlatform?.();
  return ( platform === 'android' || platform === 'ios' ) && getPlugin() != null;
}

let is_initialized = false;
let is_initializing = false;
let init_promise: Promise<boolean> | null = null;
let is_interstitial_loading = false;
let is_rewarded_loading = false;
let banner_desired_visible = false;
let banner_is_showing = false;
let banner_loaded_once = false;
let banner_show_allowed_at = 0;
let banner_visibility_timer: number | null = null;
let banner_visibility_revision = 0;
let native_listeners_attached = false;
let last_user_lifetime_revenue_sent = readStoredLifetimeRevenue();
let last_rewarded_placement = 'rewarded';
let last_interstitial_placement = 'interstitial';
let suppress_next_interstitial = false;
let init_failed_at = 0;
let online_listener_attached = false;
const late_init_listeners: Array<() => void> = [];

export function onLateInit( listener: () => void ): void
{
  late_init_listeners.push( listener );
}

function isInitBackoffActive(): boolean
{
  return init_failed_at > 0 && Date.now() - init_failed_at < INIT_RETRY_DELAY_MS;
}

function markInitFailed(): void
{
  init_failed_at = Date.now();
  if ( online_listener_attached ) return;
  online_listener_attached = true;
  NetworkManager.addOnlineListener( () => { init_failed_at = 0; } );
}

async function finishInit(): Promise<void>
{
  is_initialized = true;
  init_failed_at = 0;
  ensureNativeAdListeners();
  await UtilityPlugin.reportAppStart( 'applovin', 'success' );
}

function watchLateInit( native_init: Promise<Record<string, unknown>> ): void
{
  void native_init.then( async result =>
  {
    if ( is_initialized ) return;
    await applyPrivacyDecision( result );
    if ( result?.error )
    {
      console.warn( `${ADS_LOG_TAG} late init failed`, result.error );
      return;
    }
    await finishInit();
    console.log( `${ADS_LOG_TAG} initialized after consent flow`, result );
    for ( const listener of late_init_listeners )
    {
      try { listener(); }
      catch ( e ) { console.warn( `${ADS_LOG_TAG} late init listener failed`, e ); }
    }
    void prepareInterstitial();
    void prepareRewarded();
  } ).catch( e => console.warn( `${ADS_LOG_TAG} late init error`, e ) );
}

function readStoredLifetimeRevenue(): number | null
{
  try
  {
    const raw = localStorage.getItem( LIFETIME_REVENUE_STORAGE_KEY ) ?? localStorage.getItem( LEGACY_LIFETIME_REVENUE_STORAGE_KEY );
    if ( !raw ) return null;
    const parsed = Number( raw );
    return Number.isFinite( parsed ) ? parsed : null;
  }
  catch
  {
    return null;
  }
}

function storeLifetimeRevenue( value: number ): void
{
  try { localStorage.setItem( LIFETIME_REVENUE_STORAGE_KEY, String( value ) ); }
  catch {}
}

function toNumber( value: unknown ): number | undefined
{
  if ( typeof value === 'number' && Number.isFinite( value ) ) return value;
  if ( typeof value === 'string' )
  {
    const parsed = Number( value );
    if ( Number.isFinite( parsed ) ) return parsed;
  }
  return undefined;
}

function toStringValue( value: unknown ): string | undefined
{
  if ( value == null ) return undefined;
  const text = String( value ).trim();
  return text ? text : undefined;
}

function normalizeAdType( value: unknown ): AnalyticsAdType | string
{
  const raw = String( value ?? '' ).toLowerCase();
  if ( raw.includes( 'reward' ) ) return AnalyticsAdType.REWARDED;
  if ( raw.includes( 'inter' ) ) return AnalyticsAdType.INTERSTITIAL;
  if ( raw.includes( 'banner' ) ) return AnalyticsAdType.BANNER;
  return raw || 'unknown';
}

function trackImpressionRevenue( payload: Record<string, unknown> ): void
{
  const ad_type = normalizeAdType( payload.format );
  const revenue = toNumber( payload.revenue );
  const lifetime_revenue = toNumber( payload.revenueTotal ?? payload.revenue_total );
  const timestamp = new Date().toISOString();
  const network = toStringValue( payload.sourceName ) ?? AD_NETWORK;
  const ad_unit = toStringValue( payload.sourceUnitId ) ?? '';
  const placement =
    toStringValue( payload.placement )
    ?? ( ad_type === AnalyticsAdType.REWARDED ? last_rewarded_placement
    : ad_type === AnalyticsAdType.INTERSTITIAL ? last_interstitial_placement
    : 'banner' );
  const currency = ( toStringValue( payload.currency ) ?? 'USD' ).toUpperCase();

  if ( revenue != null )
  {
    void AppsFlyer.logAdRevenue( revenue, currency, network, {
      revenue,
      currency,
      network,
      ad_unit,
      placement,
      format: String( ad_type ),
    }, APPSFLYER_MEDIATION );
  }

  analytics.track( AnalyticsEventName.APPLOVIN_REVENUE_IMPRESSION, {
    [AnalyticsPropertyKey.TIMESTAMP]: timestamp,
    [AnalyticsPropertyKey.ADNETWORK]: AD_NETWORK,
    [AnalyticsPropertyKey.ADTYPE]: ad_type,
    [AnalyticsPropertyKey.PLACEMENTID]: placement,
    sourceId: `applovin_${ad_type}_impression`,
    ad_unit: toStringValue( payload.sourceUnitId ),
    ad_source: toStringValue( payload.sourceName ),
    max_ad_unit: toStringValue( payload.adUnitId ),
    revenue_usd: revenue,
    revenue_precision: toStringValue( payload.revenuePrecision ),
    lifetime_revenue,
    impression_depth: toNumber( payload.impressionDepth ),
  } );

  if ( ad_type === AnalyticsAdType.REWARDED || ad_type === AnalyticsAdType.INTERSTITIAL )
    void reportAdImpression( ad_type, revenue ?? undefined )
      .then( () => OfferTriggerController.report(
        ad_type === AnalyticsAdType.REWARDED ? 'rewarded_ad_completed' : 'interstitial_shown',
        { context: { ad_type } },
      ) )
      .catch( () => {} );

  if ( lifetime_revenue == null ) return;
  const previous = last_user_lifetime_revenue_sent;
  if ( previous != null && lifetime_revenue <= previous + 1e-7 ) return;

  last_user_lifetime_revenue_sent = lifetime_revenue;
  storeLifetimeRevenue( lifetime_revenue );

  analytics.track( AnalyticsEventName.APPLOVIN_REVENUE_USER_LEVEL, {
    [AnalyticsPropertyKey.TIMESTAMP]: timestamp,
    [AnalyticsPropertyKey.ADNETWORK]: AD_NETWORK,
    [AnalyticsPropertyKey.ADTYPE]: ad_type,
    [AnalyticsPropertyKey.PLACEMENTID]: placement,
    sourceId: 'applovin_user_lifetime_revenue',
    lifetime_revenue,
    ...( previous != null ? { revenue_delta: lifetime_revenue - previous } : {} ),
  } );
}

function describeAdError( e: unknown, stage: 'load' | 'show' ): Record<string, unknown>
{
  const err = ( e ?? {} ) as { code?: unknown; message?: unknown; data?: { code?: unknown } };
  const code = toStringValue( err.code ?? err.data?.code );
  const message = toStringValue( err.message ) ?? ( typeof e === 'string' ? e : undefined );
  return {
    stage,
    ...( code != null ? { error_code: code } : {} ),
    ...( message != null ? { error_message: message.slice( 0, 200 ) } : {} ),
  };
}

function ensureNativeAdListeners(): void
{
  if ( native_listeners_attached ) return;
  native_listeners_attached = true;

  document.addEventListener( 'applovin_ad_impressions', event =>
  {
    const detail = ( event as CustomEvent<Record<string, unknown>> ).detail ?? {};
    trackImpressionRevenue( detail );
  } );

  document.addEventListener( 'applovin_ad_loaded', event =>
  {
    const detail = ( event as CustomEvent<Record<string, unknown>> ).detail ?? {};
    CrashBreadcrumbs.trace( 'app', `ad loaded ${toStringValue( detail.format ) ?? '?'} ${toStringValue( detail.network ) ?? '?'}` );
    recordAdLoaded( detail );
  } );
  void flushEarlyEcpm();

  document.addEventListener( 'applovin_ad_expired_reloaded', event =>
  {
    const detail = ( event as CustomEvent<Record<string, unknown>> ).detail ?? {};
    analytics.track( AnalyticsEventName.AD_APPLOVIN_EXPIRED_RELOAD, {
      [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
      [AnalyticsPropertyKey.ADTYPE]: normalizeAdType( detail.format ),
      ad_source: toStringValue( detail.network ),
    } );
  } );
}

async function waitForAppsFlyerId( initial: string | null, timeout_ms = 5_000 ): Promise<string | null>
{
  if ( initial ) return initial;
  const started_at = Date.now();
  while ( Date.now() - started_at < timeout_ms )
  {
    const uid = await AppsFlyer.getAppsFlyerUID();
    if ( uid ) return uid;
    await new Promise( resolve => window.setTimeout( resolve, 250 ) );
  }
  return null;
}

async function startAppsFlyer( consent: AppsFlyerConsentDecision ): Promise<void>
{
  const apps_flyer = await AppsFlyer.applyConsentAndStart( consent );
  const appsflyer_id = apps_flyer.started
    ? await waitForAppsFlyerId( apps_flyer.uid )
    : null;
  if ( appsflyer_id ) S2SEventContextService.initialize( { appsflyer_id } );
  else S2SEventContextService.clearAppsFlyerId();
}

async function applyPrivacyDecision( result: AppLovinInitializationResult ): Promise<void>
{
  const decision = mapAppLovinConsentDecision( result );
  S2SEventContextService.applyExplicitConsentSnapshot( decision.snapshot );
  if ( result?.error === INIT_TIMEOUT_ERROR ) return;

  // Persist the policy decision before any downstream SDK is allowed to start.
  const waits_for_login = !AppsFlyer.hasCustomerUserId();
  const apps_flyer_start = startAppsFlyer( decision.appsFlyer );
  if ( waits_for_login )
    void apps_flyer_start
      .then( () => SessionController.syncSessionAttributes() )
      .catch( e => console.warn( `${ADS_LOG_TAG} AppsFlyer start after login failed`, e ) );
  else
    await apps_flyer_start;

  // GAID and Play Install Referrer are collected only after the CMP grants
  // ad measurement. They remain absent on denied/limited-ad-tracking devices.
  if ( decision.snapshot.ad_measurement_allowed )
  {
    const identifiers = await GoogleAttributionPlugin.getIdentifiers();
    S2SEventContextService.initialize( identifiers );
  }
  else
  {
    S2SEventContextService.clearAdMeasurementIdentifiers();
  }

  void SessionController.syncSessionAttributes();

  console.info( `${ADS_LOG_TAG} privacy decision applied`, {
    reason: decision.reason,
    region: decision.snapshot.region,
    analytics_allowed: decision.snapshot.analytics_allowed,
    ad_measurement_allowed: decision.snapshot.ad_measurement_allowed,
    ad_personalization_allowed: decision.snapshot.ad_personalization_allowed,
    appsflyer_started: AppsFlyer.isStarted(),
    appsflyer_waits_for_login: waits_for_login,
  } );
}

export async function ensureLazyInit(): Promise<boolean>
{
  if ( is_initialized ) return true;
  if ( is_initializing && init_promise ) return init_promise;

  if ( !isNative() )
  {
    console.log( `${ADS_LOG_TAG} skip init: native MaxAds plugin unavailable` );
    return false;
  }
  if ( isInitBackoffActive() ) return false;

  is_initializing = true;
  init_promise = ( async () =>
  {
    let privacy_decision_applied = false;
    try
    {
      await UtilityPlugin.setAnalyticsConsent( true );
      await UtilityPlugin.reportAppStart( 'applovin', 'start' );
      const plugin = getPlugin();
      if ( !plugin ) throw new Error( 'MaxAds plugin not available' );

      const native_init = plugin.initialize( {
        sdkKey: resolveAppLovinValue( APPLOVIN_CONFIG.sdk_key ),
        adUnitIds: {
          rewarded: resolveAppLovinValue( APPLOVIN_CONFIG.ad_unit_ids.rewarded ),
          interstitial: resolveAppLovinValue( APPLOVIN_CONFIG.ad_unit_ids.interstitial ),
          banner: resolveAppLovinValue( APPLOVIN_CONFIG.ad_unit_ids.banner ),
        },
        showConsentFormIfRequired: true,
        privacyPolicyUrl: APPLOVIN_CONFIG.privacy_policy_url,
        termsOfServiceUrl: APPLOVIN_CONFIG.terms_of_service_url,
        targetAudience: 'notchildren',
        forceTestAds: APPLOVIN_CONFIG.test_mode,
        testDeviceAdvertisingIds: APPLOVIN_CONFIG.test_device_advertising_ids,
      } );
      const raced = await Promise.race( [
        native_init,
        new Promise<typeof INIT_TIMED_OUT>( resolve => window.setTimeout( () => resolve( INIT_TIMED_OUT ), INIT_TIMEOUT_MS ) ),
      ] );

      const timed_out = raced === INIT_TIMED_OUT;
      const result: Record<string, unknown> = timed_out ? { error: INIT_TIMEOUT_ERROR } : raced;
      if ( timed_out ) watchLateInit( native_init );

      await applyPrivacyDecision( result );
      privacy_decision_applied = true;
      if ( is_initialized ) return true;

      if ( result?.error )
      {
        markInitFailed();
        await UtilityPlugin.reportAppStart( 'applovin', 'failed' );
        console.warn( `${ADS_LOG_TAG} init failed`, result.error );
        return false;
      }

      await finishInit();
      console.log( `${ADS_LOG_TAG} initialized`, result );
      void prepareInterstitial();
      return true;
    }
    catch ( e )
    {
      if ( !privacy_decision_applied )
      {
        try
        {
          await applyPrivacyDecision( {
            error: e instanceof Error ? e.message : String( e ),
            consentFlowStatus: 'Internal error',
            userConsent: 'undefined',
          } );
        }
        catch ( privacy_error )
        {
          console.error( `${ADS_LOG_TAG} failed to persist fail-closed privacy state`, privacy_error );
        }
      }
      markInitFailed();
      await UtilityPlugin.reportAppStart( 'applovin', 'failed' );
      console.warn( `${ADS_LOG_TAG} init error`, e );
      return false;
    }
    finally
    {
      is_initializing = false;
    }
  } )();

  return init_promise;
}

async function waitUntil( predicate: () => Promise<boolean>, timeout_ms: number ): Promise<boolean>
{
  const start = Date.now();
  while ( Date.now() - start < timeout_ms )
  {
    if ( await predicate() ) return true;
    await new Promise( resolve => window.setTimeout( resolve, 250 ) );
  }
  return false;
}

export async function prepareRewarded(): Promise<void>
{
  if ( is_rewarded_loading ) return;
  const plugin = getPlugin();
  if ( !await ensureLazyInit() || !plugin ) return;

  is_rewarded_loading = true;
  const ad_id = generateAdId( AnalyticsAdType.REWARDED );
  CrashBreadcrumbs.trace( 'app', 'ad request rewarded' );
  analytics.track( AnalyticsEventName.AD_APPLOVIN_REQUEST, {
    [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
    [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.REWARDED,
    [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
  } );

  try
  {
    await plugin.loadRewardedAd( { autoReload: true } );
    analytics.track( AnalyticsEventName.AD_APPLOVIN_ANSWER, {
      [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
      [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.REWARDED,
      [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
    } );
  }
  catch ( e )
  {
    analytics.track( AnalyticsEventName.AD_ERROR, {
      [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
      [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.REWARDED,
      [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
      ...describeAdError( e, 'load' ),
    } );
    console.warn( `${ADS_LOG_TAG} rewarded load failed`, e );
  }
  finally
  {
    is_rewarded_loading = false;
  }
}

export async function isRewardedAvailable(): Promise<{ available: boolean }>
{
  const plugin = getPlugin();
  if ( !AdsConfigService.isRewardedAllowed() || !await ensureLazyInit() || !plugin ) return { available: false };
  const result = await plugin.isRewardedAdLoaded().catch( () => null );
  return { available: result?.isLoaded === true };
}

export type RewardedShowResult = {
  shown: boolean;
  rewarded: boolean;
  ad_id: string | null;
};

export async function showRewardedDetailed(
  placement_id = 'rewarded',
  level = AdsConfigService.getCurrentLevel()
): Promise<RewardedShowResult>
{
  if ( !AdsConfigService.isRewardedPlacementAllowed( placement_id, level ) )
    return { shown: false, rewarded: false, ad_id: null };

  const plugin = getPlugin();
  if ( !await ensureLazyInit() || !plugin ) return { shown: false, rewarded: false, ad_id: null };

  const ready = await waitUntil( async () => ( await plugin.isRewardedAdLoaded().catch( () => null ) )?.isLoaded === true, 3000 );
  if ( !ready )
  {
    await prepareRewarded();
    return { shown: false, rewarded: false, ad_id: null };
  }

  const ad_id = getAdId( AnalyticsAdType.REWARDED );
  last_rewarded_placement = placement_id;
  analytics.track( AnalyticsEventName.AD_START, {
    [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
    [AnalyticsPropertyKey.PLACEMENTID]: placement_id,
    [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.REWARDED,
    [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
  } );
  void logAdRewardedStarted( placement_id );

  try
  {
    const result = await showFullscreenAdMuted( () => plugin.showRewardedAd( { placement: placement_id } ) );
    const rewarded = result?.isUserEarnReward === true;
    if ( rewarded )
      suppress_next_interstitial = true;
    analytics.track( AnalyticsEventName.AD_FINISH, {
      [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
      [AnalyticsPropertyKey.PLACEMENTID]: placement_id,
      [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.REWARDED,
      [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
      rewarded,
    } );
    clearAdId( AnalyticsAdType.REWARDED );
    void prepareRewarded();
    return { shown: true, rewarded, ad_id };
  }
  catch ( e )
  {
    analytics.track( AnalyticsEventName.AD_ERROR, {
      [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
      [AnalyticsPropertyKey.PLACEMENTID]: placement_id,
      [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.REWARDED,
      [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
      ...describeAdError( e, 'show' ),
    } );
    console.warn( `${ADS_LOG_TAG} rewarded show failed`, e );
    return { shown: false, rewarded: false, ad_id };
  }
}

export async function tryShowRewarded(
  placement_id = 'rewarded',
  level = AdsConfigService.getCurrentLevel()
): Promise<boolean>
{
  return ( await showRewardedDetailed( placement_id, level ) ).rewarded;
}

export async function prepareInterstitial(): Promise<void>
{
  // Preload before the first eligible level so the ad is ready to be shown
  // immediately at min_level (for example, level 10). Level/frequency checks
  // still happen in tryShowInterstitial before an actual impression.
  if ( OfferService.hasNoInterstitialAds() || !AdsConfigService.get().inter.enabled || is_interstitial_loading ) return;
  is_interstitial_loading = true;

  try
  {
    const plugin = getPlugin();
    if ( !await ensureLazyInit() || !plugin ) return;
    if ( ( await plugin.isInterstitialAdLoaded().catch( () => null ) )?.isLoaded === true ) return;

    const ad_id = generateAdId( AnalyticsAdType.INTERSTITIAL );
    CrashBreadcrumbs.trace( 'app', 'ad request interstitial' );
    analytics.track( AnalyticsEventName.AD_APPLOVIN_REQUEST, {
      [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
      [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.INTERSTITIAL,
      [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
    } );

    try
    {
      await plugin.loadInterstitialAd( { autoReload: true } );
      analytics.track( AnalyticsEventName.AD_APPLOVIN_ANSWER, {
        [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
        [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.INTERSTITIAL,
        [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
      } );
    }
    catch ( e )
    {
      analytics.track( AnalyticsEventName.AD_ERROR, {
        [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
        [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.INTERSTITIAL,
        [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
        ...describeAdError( e, 'load' ),
      } );
      console.warn( `${ADS_LOG_TAG} interstitial load failed`, e );
    }
  }
  finally
  {
    is_interstitial_loading = false;
  }
}

export async function isInterstitialReady( level = AdsConfigService.getCurrentLevel() ): Promise<boolean>
{
  const plugin = getPlugin();
  if ( OfferService.hasNoInterstitialAds() || !AdsConfigService.isInterstitialDue( level ) || !await ensureLazyInit() || !plugin ) return false;
  return ( await plugin.isInterstitialAdLoaded().catch( () => null ) )?.isLoaded === true;
}

export async function tryShowInterstitial( placement_id = 'interstitial', level = AdsConfigService.getCurrentLevel() ): Promise<boolean>
{
  if ( suppress_next_interstitial )
  {
    suppress_next_interstitial = false;
    console.log( `${ADS_LOG_TAG} interstitial suppressed after rewarded`, { placement_id } );
    return false;
  }

  const plugin = getPlugin();
  if ( OfferService.hasNoInterstitialAds() || !AdsConfigService.isInterstitialDue( level ) || !await ensureLazyInit() || !plugin ) return false;

  const ready = await isInterstitialReady( level );
  analytics.track( AnalyticsEventName.AD_OPPORTUNITY, {
    [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
    [AnalyticsPropertyKey.PLACEMENTID]: placement_id,
    [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.INTERSTITIAL,
    ready,
  } );
  if ( !ready )
  {
    void prepareInterstitial();
    return false;
  }

  const ad_id = getAdId( AnalyticsAdType.INTERSTITIAL );
  last_interstitial_placement = placement_id;
  analytics.track( AnalyticsEventName.AD_START, {
    [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
    [AnalyticsPropertyKey.PLACEMENTID]: placement_id,
    [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.INTERSTITIAL,
    [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
  } );

  try
  {
    await showFullscreenAdMuted( () => plugin.showInterstitialAd( { placement: placement_id } ) );
    analytics.track( AnalyticsEventName.AD_FINISH, {
      [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
      [AnalyticsPropertyKey.PLACEMENTID]: placement_id,
      [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.INTERSTITIAL,
      [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
    } );
    void logInterstitialShown( {
      placement: placement_id,
      network: AD_NETWORK,
    } );
    resetInterstitialLevels();
    clearAdId( AnalyticsAdType.INTERSTITIAL );
    void prepareInterstitial();
    return true;
  }
  catch ( e )
  {
    analytics.track( AnalyticsEventName.AD_ERROR, {
      [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
      [AnalyticsPropertyKey.PLACEMENTID]: placement_id,
      [AnalyticsPropertyKey.ADTYPE]: AnalyticsAdType.INTERSTITIAL,
      [AnalyticsPropertyKey.ADCUSTOMID]: ad_id,
      ...describeAdError( e, 'show' ),
    } );
    console.warn( `${ADS_LOG_TAG} interstitial show failed`, e );
    return false;
  }
}

export function setBannerInitialDelay( delay_ms: number ): void
{
  banner_show_allowed_at = Date.now() + Math.max( 0, delay_ms );
  scheduleBannerVisibilityUpdate();
}

export function setBannerVisible( visible: boolean ): void
{
  banner_desired_visible = visible && AdsConfigService.isBannerAllowed();
  banner_visibility_revision++;
  scheduleBannerVisibilityUpdate();
}

function scheduleBannerVisibilityUpdate(): void
{
  if ( banner_visibility_timer != null )
  {
    window.clearTimeout( banner_visibility_timer );
    banner_visibility_timer = null;
  }

  const delay = banner_desired_visible ? Math.max( 0, banner_show_allowed_at - Date.now() ) : 0;
  banner_visibility_timer = window.setTimeout( () =>
  {
    banner_visibility_timer = null;
    void applyBannerVisibility();
  }, delay );
}

async function applyBannerVisibility(): Promise<void>
{
  if ( banner_desired_visible && AdsConfigService.isBannerAllowed() ) await showBottomSmartBannerWithRetry();
  else await hideBanner();
}

export async function preloadBanner(): Promise<void>
{
  if ( !AdsConfigService.isBannerAllowed() ) return;
  const plugin = getPlugin();
  if ( !await ensureLazyInit() || !plugin || banner_loaded_once ) return;

  try
  {
    await plugin.loadBannerAd( {
      adSize: 'S',
      autoReload: false,
      refreshInterval: AdsConfigService.get().banner.refresh_interval_seconds,
    } );
    banner_loaded_once = true;
  }
  catch ( e )
  {
    console.warn( `${BANNER_LOG_TAG} preload failed`, e );
  }
}

export async function showBottomSmartBannerWithRetry(): Promise<void>
{
  if ( !AdsConfigService.isBannerAllowed() || banner_is_showing ) return;
  const plugin = getPlugin();
  if ( !await ensureLazyInit() || !plugin ) return;

  const visibility_revision = banner_visibility_revision;

  try
  {
    await plugin.loadBannerAd( {
      adSize: 'S',
      autoReload: true,
      refreshInterval: AdsConfigService.get().banner.refresh_interval_seconds,
    } );
    await plugin.showBannerAd();
    banner_loaded_once = true;
    banner_is_showing = true;
    if ( visibility_revision !== banner_visibility_revision || !banner_desired_visible )
      await hideBanner();
  }
  catch ( e )
  {
    console.warn( `${BANNER_LOG_TAG} show failed`, e );
  }
}

export async function hideBanner(): Promise<void>
{
  const plugin = getPlugin();
  if ( !plugin || !banner_is_showing ) return;
  try
  {
    await plugin.hideBannerAd();
    banner_is_showing = false;
  }
  catch ( e )
  {
    console.warn( `${BANNER_LOG_TAG} hide failed`, e );
  }
}

export async function showMediationDebugger(): Promise<void>
{
  const plugin = getPlugin();
  if ( !await ensureLazyInit() || !plugin?.showMediationDebugger ) return;
  await plugin.showMediationDebugger().catch( e => console.warn( `${ADS_LOG_TAG} mediation debugger failed`, e ) );
}

export function initLazyWithDelay( delay_ms = 3000 ): void
{
  if ( !isNative() ) return;
  window.setTimeout( () =>
  {
    void ensureLazyInit().then( ok =>
    {
      if ( !ok ) return;
      void prepareInterstitial();
      void prepareRewarded();
    } );
  }, Math.max( 0, delay_ms ) );
}

export function initLazyImmediately(): void
{
  initLazyWithDelay( 0 );
}

export async function prepareAllAdsAndWait(): Promise<void>
{
  await ensureLazyInit();
  await Promise.allSettled( [ prepareInterstitial(), prepareRewarded() ] );
}

export function setupBannerOnLevelUp(): void
{
  scheduleBannerVisibilityUpdate();
}

export function tryShowBannerAfterLevelReached(): void
{
  scheduleBannerVisibilityUpdate();
}
